/**
 * partnerScope — what a partner-scoped user is offered (RBAC-3.92, RBAC-3.93, RBAC-3.95).
 *
 * RBAC-3.90 made outside partner staff ("partner-scoped": a row in `user_partner_scopes`) read only
 * their partner's projects and write nothing project-shaped. RBAC-3.91 made joules-admin-backend
 * refuse their writes with 403, including project email. The project screen still offered Compose,
 * Edit, notes, tasks and uploads, which then failed. This decides whether to offer them.
 *
 * ⚠️ THIS IS NOT THE GATE. The database's `partner_scoped()` and the backend's
 * `require_project_access(..., write=True)` are. This only stops the screen offering a button that
 * will be refused, which is why an error reads as "not scoped": hiding Compose from every user on a
 * network blip would remove a working control, while showing it to a partner costs one 403.
 */

export const PARTNER_EMAIL_READ_ONLY_NOTE =
  'You have read-only access to this project, so you can read its email but not send any.';

export const PARTNER_SMS_READ_ONLY_NOTE =
  'You have read-only access to this project, so you can read its text messages but not send any.';

export const PARTNER_COMMENT_READ_ONLY_NOTE =
  'You have read-only access to this project, so you can read its comments but not add any.';

export const PARTNER_PROJECT_READ_ONLY_NOTE =
  'You have read-only access to this project, so you can view it but not change it.';

export interface PartnerScopeState {
  loading: boolean;
  scoped: boolean;
}

/** Offer a project write only once the answer is known and the caller is not partner-scoped. */
export function canWriteProject({ loading, scoped }: PartnerScopeState): boolean {
  return !loading && !scoped;
}

/**
 * RBAC-3.95: the contract actions that only read. Everything else a card offers (edit, send, resend,
 * remind, cancel) writes, and a partner-scoped user is offered none of it.
 */
export const READ_ONLY_CONTRACT_ACTIONS: readonly string[] = ['view', 'view_pdf', 'view_history'];

/** What a contract card offers: every action to a writer, the read-only ones to anyone else. */
export function offeredContractActions<A extends string>(actions: readonly A[], canWrite: boolean): A[] {
  return canWrite ? [...actions] : actions.filter((a) => READ_ONLY_CONTRACT_ACTIONS.includes(a));
}

/** Compose and Reply: a project write like any other. */
export const canSendProjectEmail = canWriteProject;

type RpcResult = { data: unknown; error: unknown };

/** `partner_scoped()` for the signed-in user. True only for a literal `true`. */
export async function readPartnerScoped(rpc: () => PromiseLike<RpcResult>): Promise<boolean> {
  try {
    const { data, error } = await rpc();
    return !error && data === true;
  } catch {
    return false;
  }
}
